import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import NamedLink from '../NamedLink/NamedLink';

import css from './TesterTermsOfService.module.css';

const TesterTermsOfService = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      {/* <p className={css.lastUpdated}>Last updated: September 11, 2025</p> */}

      <p>
        Thank you for contributing as a tester on our marketplace. These Tester Terms of Service
        govern your provision of testing services. They are designed to be clear and respectful
        while protecting all parties. By registering as a tester or providing any services,
        you agree to these terms in addition to our general{' '}
        <NamedLink name="TermsOfServicePage">Terms of Service</NamedLink> and{' '}
        <NamedLink name="PrivacyPolicyPage">Privacy Policy</NamedLink>.
      </p>

      <p>
        We ask that you always act professionally, ethically, and within the law. If you are ever
        unsure about a request, decline it and contact us. We want this to be a safe and
        straightforward experience for everyone.
      </p>

      <h2>Independent Contractor Status</h2>
      <hr />
      <p>
        You provide services as an independent contractor, not as our employee, agent, partner, or
        joint venturer. You are solely responsible for determining how to perform your services and
        for complying with all laws that apply to you. As transactions are between you and the customer, you are solely responsible any liabilities that may arise from the transaction.
      </p>

      <h2>Legal Compliance and Permitted Conduct</h2>
      <hr />
      <p>
        You agree to comply with all applicable laws, rules, and regulations in every jurisdiction
        where you operate. Without limitation, you must not engage in harassment, stalking,
        impersonation, defamation, non‑consensual intimate contact, trespass, unlawful recording,
        hacking, unauthorized access, or any other illegal or unethical activity. You will obtain
        any licenses, registrations, or permits required to provide your services.
      </p>

      <h2>Financial Responsibility</h2>
      <hr />
      <p>
        You bear full responsibility for all costs, expenses, liabilities, taxes, penalties, fines,
        chargebacks, refunds, adjustments, or claims that arise from or relate to your services,
        conduct, or accounts. Where a buyer is issued a refund, credit, or adjustment due to your
        acts, omissions, quality issues, policy violations, or legal non‑compliance, you authorize
        us to deduct the corresponding amount (including any processing fees or costs) from your
        balances, future payouts, or payment methods on file, without prior notice or additional
        consent.
      </p>

      <h2>No Lawsuits; Dispute Resolution</h2>
      <hr />
      <p>
        To the fullest extent permitted by law, you agree not to initiate or participate in any
        class action, collective action, or representative proceeding against us. Any dispute
        between you and us will be resolved individually through binding arbitration or another
        agreed dispute mechanism where permitted. If arbitration is not permitted, you consent to
        the exclusive jurisdiction and venue specified in our general Terms of Service.
      </p>

      <h2>Indemnification and Release; You Pay to Protect Us</h2>
      <hr />
      <p>
        <strong>If you are sued, threatened with a lawsuit, or otherwise involved in any legal
        claim, dispute, investigation, or proceeding of any kind arising out of or related to
        your services or use of the marketplace, you cannot hold us liable for anything.</strong>{' '}
        We, our affiliates, officers, directors, employees, and agents shall have no liability to
        you whatsoever in connection with any such lawsuit, claim, or proceeding, regardless of who
        brought it, what it alleges, or how it is resolved.
      </p>
      <p>
        <strong>You will pay to protect us.</strong> You will defend, indemnify, and hold harmless
        us and our affiliates, officers, directors, employees, and agents from and against any
        claims, lawsuits, investigations, damages, losses, liabilities, costs, and expenses
        (including attorneys’ fees, court costs, expert fees, and all other costs of defense)
        arising out of or related to your services, your conduct, your violation of these terms or
        the law, any dispute between you and a buyer or third party, or any lawsuit, claim, demand,
        or proceeding brought against you or against us in connection with your services. You will
        promptly pay all amounts necessary to defend us as they are incurred (not only after a
        final judgment). We may, at our option, control the defense and settlement of any matter
        subject to indemnification, and you will cooperate fully and pay all related costs. You
        will not settle any claim in a manner that imposes any obligation on us without our prior
        written consent. To the maximum extent allowed by law, you release us from any claims you
        have or may have arising out of your services or interactions on the marketplace.
      </p>

      <h2>Payment Adjustments, Chargebacks, and Offsets</h2>
      <hr />
      <p>
        You acknowledge that refunds, reversals, and chargebacks may occur. You authorize us to
        withhold, delay, or adjust payouts to you to cover potential or actual refunds, chargebacks,
        investigations, or policy violations. We may set off any amounts you owe us against any
        amounts we owe you.
      </p>

      <h2>Pricing, Commissions, and Fees</h2>
      <hr />
      <p>
        We reserve the right, in our sole and absolute discretion, to establish, modify, increase,
        decrease, restructure, or eliminate any and all pricing at any time, without any advance
        notice to you whatsoever and without your consent. This includes, without limitation, the
        customer commission, the loyalty test take rate, tester prices and any minimum or maximum
        price limits, service fees, processing fees, payout fees, promotional or discounted pricing,
        currency and rounding treatment, and anything else dealing with pricing, rates, fees, or
        revenue share in any respect whatsoever. Such changes may apply to future transactions
        immediately upon taking effect, and your continued access to or provision of services after
        any change constitutes your acceptance of it. We are under no obligation to notify you of
        any pricing change, to explain it, or to maintain any particular price, rate, fee, or
        revenue share for any period of time. To the fullest extent permitted by law, you waive any
        claim arising out of or relating to any such change.
      </p>

      <h2>Quality, Accuracy, and Honesty</h2>
      <hr />
      <p>
        You will provide services with care, accuracy, and honesty. You will not make false claims
        about outcomes, fabricate communications, or misrepresent identities. If the tested party
        does not respond, that may constitute a completed test under the buyer’s selected package,
        as described in the listing.
      </p>

      <h2>Confidentiality and Data Protection</h2>
      <hr />
      <p>
        You will protect personal data and confidential information, collect only what is necessary
        to perform the service, and comply with our Privacy Policy. Do not disclose or publish any
        private communications or sensitive information except as required by law.
      </p>

      <h2>Communication Monitoring and On‑Platform Payments</h2>
      <hr />
      <p>
        To protect users, ensure quality, prevent fraud, and enforce these terms, we may monitor,
        access, review, and retain your communications with customers conducted on the site at any
        point, with or without notice. You should have no expectation of privacy in communications
        you conduct through our platform. All payments for services must be processed through the
        site using our approved payment methods. Soliciting, arranging, requesting, or accepting
        payment through any outside or off‑platform method is strictly prohibited. We reserve the
        right, in our sole discretion, to temporarily or permanently ban or suspend your account,
        withhold payouts, and take any other action we deem appropriate if we believe you have used
        or attempted to use an outside payment method.
      </p>

      <h2>Prohibited Content and Methods</h2>
      <hr />
      <p>
        You must not use threats, extortion, non‑consensual explicit content, deepfakes, malware,
        social‑engineering for account compromise, or any method that violates platform policies or
        laws. Do not create fake accounts in a way that breaches other platforms’ terms.
      </p>

      <h2>Third‑Party Platforms and Terms</h2>
      <hr />
      <p>
        If you use messaging apps, social networks, or other third‑party platforms while providing
        services, you will comply with all applicable third‑party terms, policies, and community
        guidelines. You are solely responsible for any enforcement actions, account bans,
        limitations, or losses arising from your use of those platforms. We are not affiliated with
        those platforms and do not grant you any rights with respect to them. You will not imply
        that you act on behalf of any third party, and you will not use scraping, automated access,
        or other restricted methods unless expressly permitted by the platform’s terms. You will
        bear all costs and liabilities resulting from any breach of third‑party terms.
      </p>

      <h2>Eligibility, Vetting, and Insurance</h2>
      <hr />
      <p>
        You represent and warrant that you are at least 18 years old (and otherwise of legal age) and have the legal capacity to provide
        services in your jurisdiction. You agree to provide accurate, current information and to
        cooperate with identity, background, compliance, or other vetting checks we reasonably
        request. We may restrict or suspend access while such checks are pending or if results are
        unsatisfactory in our discretion. You are solely responsible for maintaining any insurance
        required by law or reasonably appropriate for your services and will provide proof of
        coverage upon request. The absence of insurance does not shift liability to us.
      </p>

      <h2>Marketing and Use of Public Profile</h2>
      <hr />
      <p>
        If your tester profile is set to public, you grant us a worldwide, royalty‑free, sublicensable,
        transferable, and perpetual license to use, reproduce, display, adapt, publish, distribute,
        and otherwise exploit your public profile content (including your display name, photos,
        listing titles and descriptions, and ratings) in our advertising, marketing, promotional,
        and informational materials in any media now known or later developed. You acknowledge and
        agree that your public profile may or may not be used in such materials at our discretion,
        without notice or additional approval and without compensation, to the fullest extent
        permitted by law. We may edit or format public profile content for presentation without
        materially distorting its meaning. If you do not wish to be featured, set your profile to
        private or contact us where opt‑out is required by applicable law.
      </p>

      <h2>Account Security and Access</h2>
      <hr />
      <p>
        You are responsible for safeguarding your credentials and devices. You will not share your
        account or access with others. You are responsible for activity under your account.
      </p>

      <h2>Taxes and Reporting</h2>
      <hr />
      <p>
        You are solely responsible for all taxes, filings, and reporting obligations related to your
        earnings. We may provide information returns as required by law.
      </p>

      <h2>Policy Compliance; Investigations</h2>
      <hr />
      <p>
        You will comply with our policies and reasonable requests for information. We may suspend,
        withhold payouts, or terminate access if we believe you have violated these terms or the
        law, or if needed to comply with legal obligations or protect users.
      </p>

      <h2>Limitation of Liability</h2>
      <hr />
      <p>
        To the maximum extent permitted by law, we will not be liable to you for any indirect,
        incidental, special, consequential, exemplary, or punitive damages, or for lost profits,
        revenues, or data, arising from or related to your services or these terms.
      </p>

      <h2>Termination</h2>
      <hr />
      <p>
        Either party may terminate your access to provide services at any time. Sections that by
        their nature should survive termination will survive, including indemnification, limitation
        of liability, dispute resolution, and payment adjustments.
      </p>

      <h2>Changes to These Terms</h2>
      <hr />
      <p>
        We reserve the right to modify, amend, or update these Tester Terms of Service at any time
        in our sole discretion, with or without notice. Any changes will be effective immediately
        upon posting to the website. Your continued access to or provision of services after any
        changes are made constitutes your acceptance of the new terms. It is your responsibility to
        review these terms periodically for updates. If you do not agree to any modifications, your
        sole remedy is to discontinue providing services.
      </p>

      <h2>Contact</h2>
      <hr />
      <p>
        Questions? Contact us at <a href="mailto:contact@loyalty-test.com">contact@loyalty-test.com</a>.
      </p>
    </div>
  );
};

TesterTermsOfService.defaultProps = {
  rootClassName: null,
  className: null,
};

const { string } = PropTypes;

TesterTermsOfService.propTypes = {
  rootClassName: string,
  className: string,
};

export default TesterTermsOfService;


